import React, { useEffect, useState } from 'react';
import TextField from '@mui/material/TextField';
import StbButton from '../security-toolbox-ui/StbButton';
import StbModal from '../security-toolbox-ui/StbModal';
import { useInjectLabStep, useLabPools, usePreviewLabInjection } from '../../hooks/lab-hooks';
import {
  LabInjectionPreview,
  LabScenario,
  LabScenarioStep,
  labBlastRadiusLabel,
  labInsertModeLabel,
  labStepInjectionBlockers,
} from '../../support/labService';
import { LabBlockerList, LabStatusTag, labErrorMessage } from './LabShared';

export type LabInjectionModalProps = {
  open: boolean;
  scenario: LabScenario;
  step: LabScenarioStep | null;
  onClose: () => void;
  onInjected?: (injectionId: number) => void;
};

/**
 * Push a step at labs that are already running.
 *
 * Two things this insists on, because both come from how the backend behaves:
 *
 * **Dry run first.** The preview lists the runs the sweep would reach right now. It is a
 * POST and its answer goes stale as runs progress, so it is deliberately not cached and the
 * apply button stays disabled until a preview has been seen. Pushing a disruptive step at an
 * unknown number of live pods during an event is not a thing to do from muscle memory.
 *
 * **A scope.** With a pool selected the sweep is limited to that pool's sessions, and a pool
 * contact can run it. Without one it reaches every open run of the scenario, and only staff
 * can do that.
 */
const LabInjectionModal: React.FC<LabInjectionModalProps> = ({
  open,
  scenario,
  step,
  onClose,
  onInjected,
}) => {
  const [poolSlug, setPoolSlug] = useState('');
  const [reason, setReason] = useState('');
  const [preview, setPreview] = useState<LabInjectionPreview | null>(null);
  const [formError, setFormError] = useState<string | null>(null);

  const { data: pools } = useLabPools(true, { enabled: open });
  const previewInjection = usePreviewLabInjection();
  const inject = useInjectLabStep();

  useEffect(() => {
    if (!open) return;

    setPoolSlug('');
    setReason('');
    setPreview(null);
    setFormError(null);
    previewInjection.reset();
    inject.reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, step?.id]);

  const blockers = step ? labStepInjectionBlockers(step) : ['No step selected.'];

  const request = () => ({
    step_id: step?.id,
    ...(poolSlug ? { pool: poolSlug } : {}),
    ...(reason.trim() ? { reason: reason.trim() } : {}),
  });

  const handlePreview = async () => {
    setFormError(null);
    setPreview(null);

    try {
      setPreview(await previewInjection.mutateAsync(request()));
    } catch {
      // Surfaced through previewInjection.error.
    }
  };

  const handleSubmit = async () => {
    if (blockers.length) {
      setFormError('This step is not eligible for injection.');
      return;
    }

    if (!preview) {
      setFormError('Run the dry run first, so you can see which pods this reaches.');
      return;
    }

    // The wide, disruptive case is the one worth a sentence of justification in the record.
    if (!reason.trim() && (step?.disruptive || step?.blast_radius !== 'device')) {
      setFormError('Give a reason. This is recorded against every run the step lands in.');
      return;
    }

    setFormError(null);

    try {
      const injection = await inject.mutateAsync(request());
      onInjected?.(injection.id);
      onClose();
    } catch {
      // Surfaced through inject.error.
    }
  };

  const submitError =
    formError ||
    (previewInjection.isError ? labErrorMessage(previewInjection.error) : null) ||
    (inject.isError ? labErrorMessage(inject.error) : null);

  const runCount = preview?.runs?.length ?? 0;

  return (
    <StbModal
      open={open}
      role="update"
      title={step ? `Push ${step.key} to running pods` : 'Push a step'}
      size="large"
      scrollable
      submitLabel={runCount ? `Apply to ${runCount} run${runCount === 1 ? '' : 's'}` : 'Apply'}
      submitButtonRole="delete"
      submitDisabled={!!blockers.length || !preview || inject.isPending}
      suppressCloseOnSubmit
      onSubmit={handleSubmit}
      onClose={async () => onClose()}
      apiResponseSection={
        submitError ? <p className="m-0 text-sm text-amber-800">{submitError}</p> : undefined
      }
    >
      {!step ? (
        <p className="m-0 text-sm text-gray-600">No step selected.</p>
      ) : (
        <div className="flex flex-col gap-4">
          <div className="rounded border border-gray-200 bg-gray-50 p-3 text-sm">
            <div className="font-medium">{step.name}</div>
            <div className="mt-1 flex flex-wrap gap-1">
              <LabStatusTag label={labInsertModeLabel(step.insert_mode)} />
              <LabStatusTag
                label={labBlastRadiusLabel(step.blast_radius)}
                sentiment={step.blast_radius === 'shared' ? 'danger' : 'info'}
              />
              {step.disruptive && <LabStatusTag label="Disruptive" sentiment="warning" />}
              {step.destructive && <LabStatusTag label="Destructive" sentiment="danger" />}
            </div>
            <p className="m-0 mt-2 text-xs text-gray-600">
              {step.insert_mode === 'mandatory'
                ? 'Mandatory: it lands on every open run regardless of how far along it is.'
                : 'Catch-up: anyone already past its anchor step is skipped, so their progress is not disturbed.'}
            </p>
          </div>

          <LabBlockerList blockers={blockers} heading="This step cannot be pushed:" />

          <label className="flex flex-col gap-1 text-sm">
            <span className="font-medium text-gray-700">Scope</span>
            <select
              className="rounded border border-gray-300 p-2 text-sm"
              value={poolSlug}
              onChange={(evt) => {
                setPoolSlug(evt.target.value);
                // The scope decides which runs are reached, so an old preview is now wrong.
                setPreview(null);
              }}
            >
              <option value="">Every open run of this scenario (staff only)</option>
              {(pools || [])
                .filter((pool) => !pool.scenario || pool.scenario === scenario.id)
                .map((pool) => (
                  <option value={pool.slug} key={pool.slug}>
                    {pool.name}
                  </option>
                ))}
            </select>
            <span className="text-xs text-gray-500">
              A pool limits the sweep to that pool&apos;s sessions and lets its contact run it.
              Unscoped reaches everything and is restricted to staff.
            </span>
          </label>

          <TextField
            label="Reason"
            value={reason}
            onChange={(evt) => setReason(evt.target.value)}
            multiline
            minRows={2}
            helperText="Recorded against the injection and every run it touches."
            fullWidth
          />

          <div className="flex items-center gap-2">
            <StbButton
              role="request"
              size="small"
              loading={previewInjection.isPending}
              disabled={!!blockers.length}
              onClick={async () => handlePreview()}
            >
              Dry run
            </StbButton>
            <span className="text-xs text-gray-500">
              Nothing is changed. This lists the runs the sweep would reach right now.
            </span>
          </div>

          {preview && (
            <div className="rounded border border-gray-200 p-3">
              <div className="text-sm font-medium">
                {runCount
                  ? `${runCount} run${runCount === 1 ? '' : 's'} would be reached`
                  : 'No open runs would be reached'}
              </div>

              {runCount > 0 && (
                <div className="mt-2 max-h-60 overflow-y-auto">
                  <table className="w-full text-left text-sm">
                    <thead>
                      <tr className="border-b border-gray-200 text-xs uppercase tracking-wide text-gray-500">
                        <th className="px-2 py-1 font-semibold">Session</th>
                        <th className="px-2 py-1 font-semibold">Run</th>
                        <th className="px-2 py-1 font-semibold">State</th>
                        <th className="px-2 py-1 font-semibold">Phase</th>
                      </tr>
                    </thead>
                    <tbody>
                      {preview.runs.map((run) => (
                        <tr key={run.run_id} className="border-b border-gray-100">
                          <td className="px-2 py-1 font-mono text-xs">{run.session_key}</td>
                          <td className="px-2 py-1">{run.run_id}</td>
                          <td className="px-2 py-1">{run.state}</td>
                          <td className="px-2 py-1">{run.phase}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {!runCount && (
                <p className="m-0 mt-1 text-xs text-gray-600">
                  Applying anyway records the injection, and re-applying later will pick up pods
                  that start after now.
                </p>
              )}
            </div>
          )}
        </div>
      )}
    </StbModal>
  );
};

export default LabInjectionModal;
